import { Effect, Schema, SchemaIssue, SchemaTransformation } from "effect";

/**
 * Effect 3 encoded `Schema.Unknown` as the identity, so a `Date`, an `Option`, a class instance,
 * an `undefined` field or `NaN` reached the response body and `JSON.stringify` serialized it.
 * Effect 4's JSON codec for `Schema.Unknown` only accepts values that already are plain JSON and
 * fails the whole response otherwise. These schemas keep the Effect 3 wire format.
 */
const viaJsonStringify = <T, E extends Schema.Json>() =>
  SchemaTransformation.transformEffect<T, E>({
    decode: (input) => Effect.succeed(input as unknown as T),
    encode: (input, options) => {
      try {
        // `JSON.stringify` returns undefined for a top-level `undefined`, despite its lib type.
        const json = JSON.stringify(input) as string | undefined;
        return Effect.succeed((json === undefined ? null : JSON.parse(json)) as E);
      } catch (error) {
        return Effect.fail(
          new SchemaIssue.InvalidValue(
            { message: `Not serializable as JSON: ${String(error)}` },
            input,
            options
          )
        );
      }
    },
  });

/** An untyped value, encoded the way `JSON.stringify` writes it. */
export const JsonUnknown = Schema.Json.pipe(
  Schema.decodeTo(Schema.Unknown, viaJsonStringify<unknown, Schema.Json>())
);

const UnknownRecord = Schema.Record(Schema.String, Schema.Unknown);
const JsonRecord = Schema.Record(Schema.String, Schema.Json);

/**
 * A string-keyed record of untyped values, encoded the way `JSON.stringify` writes it, so a key
 * whose value is `undefined` is dropped rather than written as `null`.
 */
export const JsonUnknownRecord = JsonRecord.pipe(
  Schema.decodeTo(
    UnknownRecord,
    viaJsonStringify<typeof UnknownRecord.Encoded, typeof JsonRecord.Type>()
  )
);
